import { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '../store'
import { useFeatureFlagsStore } from '@/store/featureFlagsStore'
import { trackEvent } from '@/utils/analytics'
import { apiService } from '@/service/api'
import { hasCompletedOnboarding, markOnboardingCompleted } from '@/utils/onboarding'
import {
  setPendingDestination,
  peekPendingDestination,
  clearPendingDestination,
} from '@/utils/pendingDestination'
import {
  FEATURE_ACCESS_STALE_EVENT,
  featuresForPath,
  firstAllowedPath,
  notifyFeatureBlocked,
} from '@/features/featureAccess/features'
import { useFeatureAccess } from '@/features/featureAccess/useFeatureAccess'
import { readOrgParam, withoutOrg } from '@/utils/orgLink'


type LoginProtectedRouteProps = PropsWithChildren

const LoginProtectedRoute = ({ children }: LoginProtectedRouteProps) => {
  const { userLogged, user, initializeAuth, organizations, activeOrganizationId, switchTenant } =
    useAppStore()
  const [sessionLoading, setSessionLoading] = useState(true)
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const onboardingRedirectTracked = useRef(false)

  useEffect(() => {
    let cancelled = false
    const checkSession = async () => {
      // initializeAuth is idempotent - handles its own state management
      // and early-returns if already initialized, updating state from current Keycloak state
      await initializeAuth()

      // Hydrate the durable onboarding-completion flag from the server BEFORE we
      // stop the spinner, so an already-onboarded user isn't bounced to
      // /onboarding just because their localStorage was cleared, or they're on a
      // new device / browser (FS-606). localStorage is only a synchronous
      // fast-path cache. Only the ambiguous case needs a round-trip: a user who
      // HAS a tenant but whose local flag is missing. Tenant-less users always
      // onboard (to get a workspace) and a present flag is trusted as-is — both
      // decided synchronously below without a fetch.
      const current = useAppStore.getState().user
      if (current?.userId && current.tenant_id && !hasCompletedOnboarding(current.userId)) {
        try {
          const { completed } = await apiService.getOnboardingCompleted()
          if (completed) markOnboardingCompleted(current.userId)
        } catch {
          // Network / endpoint failure — fall back to the local flag. Worst case
          // the user re-onboards; the completion write is idempotent server-side.
        }
      }

      if (!cancelled) setSessionLoading(false)
    }

    checkSession()
    // Only depend on initializeAuth - it's idempotent and handles all auth state
    // Avoid including keycloakInitialized to prevent double execution on state change
    return () => {
      cancelled = true
    }
  }, [initializeAuth])

  // Per-person feature flags: load this user's areas, and bounce them off a
  // page whose area is switched off.
  const loadFeatureAccess = useAppStore((s) => s.loadFeatureAccess)
  const { isEnabled, loaded: featuresLoaded } = useFeatureAccess()
  const userId = user?.userId
  useEffect(() => {
    if (userLogged && userId) void loadFeatureAccess(userId)
  }, [userLogged, userId, loadFeatureAccess])
  // Flags change while the app is open. Re-check (rate-limited in the slice) when
  // the API refuses a feature, when the tab regains focus, and on each gated page.
  const routeFeatures = featuresForPath(location.pathname)
  const gatedRoute = routeFeatures.length > 0
  useEffect(() => {
    if (!userLogged || !userId) return
    const reload = () => void loadFeatureAccess(userId, true)
    window.addEventListener(FEATURE_ACCESS_STALE_EVENT, reload)
    window.addEventListener('focus', reload)
    return () => {
      window.removeEventListener(FEATURE_ACCESS_STALE_EVENT, reload)
      window.removeEventListener('focus', reload)
    }
  }, [userLogged, userId, loadFeatureAccess])
  useEffect(() => {
    if (userLogged && userId && gatedRoute) void loadFeatureAccess(userId, true)
  }, [userLogged, userId, gatedRoute, location.pathname, loadFeatureAccess])
  // Feature flags (`GET /v2/me/flags`): loaded once after login and re-read, rate-limited in the
  // slice, when the tab regains focus (the server evaluates them, the browser no longer does).
  const loadServerFlags = useFeatureFlagsStore((s) => s.loadServerFlags)
  const clearServerFlags = useFeatureFlagsStore((s) => s.clearServerFlags)
  useEffect(() => {
    if (userLogged && userId) void loadServerFlags(userId)
  }, [userLogged, userId, loadServerFlags])
  useEffect(() => {
    if (!userLogged || !userId) return
    const reload = () => void loadServerFlags(userId, true)
    window.addEventListener('focus', reload)
    return () => window.removeEventListener('focus', reload)
  }, [userLogged, userId, loadServerFlags])
  const routeBlocked = featuresLoaded && routeFeatures.some((f) => !isEnabled(f))
  useEffect(() => {
    if (routeBlocked) notifyFeatureBlocked()
  }, [routeBlocked])

  // Org links (`?org=<id>`, utils/orgLink): a member whose active organisation
  // is another one is switched to the linked one in place (no reload: nothing
  // of the old organisation has loaded yet), the organisation-dependent feature
  // list is reloaded, and the page opens without the parameter. A failed switch
  // gets a toast and the parameter is dropped so it is not retried. A
  // non-member is left alone.
  const fullPath = location.pathname + location.search + location.hash
  const linkedOrg = userLogged ? readOrgParam(location.search) : null
  const linkedOrgIsMember = !!linkedOrg && (organizations ?? []).some((o) => o.id === linkedOrg)
  const needsOrgSwitch =
    !!linkedOrg && linkedOrgIsMember && linkedOrg !== activeOrganizationId
  const orgLinkHandled = useRef<string | null>(null)
  // Held from the start of the switch until the feature list is cleared and the
  // URL is clean, so the page never mounts in between.
  const [orgLinkSwitching, setOrgLinkSwitching] = useState(false)
  const clearFeatureAccess = useAppStore((s) => s.clearFeatureAccess)
  useEffect(() => {
    if (sessionLoading || !linkedOrg || !needsOrgSwitch) return
    if (orgLinkHandled.current === fullPath) return
    orgLinkHandled.current = fullPath
    const destination = withoutOrg(fullPath)
    setOrgLinkSwitching(true)
    switchTenant(linkedOrg, { reload: false })
      .then(() => {
        clearFeatureAccess()
        clearServerFlags()
        if (userId) {
          void loadFeatureAccess(userId)
          void loadServerFlags(userId)
        }
      })
      .catch((err: unknown) => {
        console.error('[tenant-switch] org link failed:', err)
        toast.error(t('account:tenant.switch_failed'), {
          description: err instanceof Error ? err.message : String(err),
        })
      })
      .finally(() => {
        navigate(destination, { replace: true })
        setOrgLinkSwitching(false)
      })
  }, [
    sessionLoading,
    linkedOrg,
    needsOrgSwitch,
    fullPath,
    switchTenant,
    clearFeatureAccess,
    clearServerFlags,
    loadFeatureAccess,
    loadServerFlags,
    userId,
    navigate,
    t,
  ])

  // Show a spinner (not a white blank screen) during initial session
  // verification. The OAuth callback that lands here (e.g.
  // /intelligent-board#code=...) triggers a token exchange that can take a
  // beat on first login; rendering `null` looked like a frozen blank page and
  // led users to refresh manually (FS-606).
  // Also held while an org link switches, so a page in the wrong organisation
  // never mounts and fires requests that would 404.
  if (sessionLoading || needsOrgSwitch || orgLinkSwitching) {
    return (
      <div className="flex items-center justify-center min-h-screen w-full">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400 dark:text-gray-500" />
      </div>
    )
  }

  if (!userLogged) {
    // FS-663: remember where they were going so the post-Keycloak landing can
    // resume it. Without this a share link always ends on the board library.
    // Skip '/' itself — a plain visit to the login page must not overwrite a
    // genuine pending share destination.
    if (location.pathname !== '/') {
      setPendingDestination(location.pathname + location.search + location.hash)
    }
    return <Navigate to="/" replace />
  }

  // Onboarding gate. Two reasons a user is sent through onboarding:
  //  1. No tenant_id — the app is unusable (every tenant-scoped request 400s
  //     with "no tenant association"), so a tenant-less user is ALWAYS routed to
  //     onboarding, which auto-creates a workspace at the end of the flow
  //     (OnboardingV2.finishOnboarding). This must ignore the local
  //     "completed" flag: a user who clicked through the old questionnaire (which
  //     never created a tenant) is marked complete yet still has no tenant, and
  //     must be re-onboarded to get one.
  //  2. Has a tenant but hasn't finished the profile questionnaire — e.g. an
  //     invited user who joined the inviter's org. They onboard once; the local
  //     completion flag then releases the gate.
  // The flag only short-circuits case 2, so completing onboarding gives the user
  // a tenant and the gate opens without bouncing back to /onboarding (no
  // infinite redirect loop — FS-606).
  const isOnboardingPage = location.pathname.startsWith('/onboarding')
  const needsOnboarding =
    !!user && (!user.tenant_id || !hasCompletedOnboarding(user.userId))

  if (needsOnboarding && !isOnboardingPage) {
    if (!onboardingRedirectTracked.current) {
      onboardingRedirectTracked.current = true
      trackEvent('auth-signup_gate_shown', {
        trigger: user.tenant_id ? 'profile_incomplete' : 'no_tenant_id',
        page_path: location.pathname,
      })
    }
    return <Navigate to="/onboarding" replace />
  }

  // The page renders while the list loads (the API still refuses a hidden area),
  // then redirects if the loaded list hides it.
  if (routeBlocked) {
    // A blocked share link must not bring the next login back here.
    if (peekPendingDestination() === location.pathname + location.search + location.hash) {
      clearPendingDestination()
    }
    return <Navigate to={firstAllowedPath(isEnabled)} replace />
  }

  // FS-663: they have arrived at the destination, so the pending resume is
  // spent. Belt-and-braces — the TTL would expire it anyway — but it stops a
  // stale value from redirecting their NEXT login.
  if (peekPendingDestination() === location.pathname + location.search + location.hash) {
    clearPendingDestination()
  }

  return children
}

export default LoginProtectedRoute
